import React, { memo } from "react";
import CardDefault from "ui/components/Cards/CardDefault";
import {
  ConsumoDto,
  ConsumoUnidade,
} from "core/features/consumo/typings/index";
import { useState } from "react";
import { Flex, Text, Box, Spinner } from "@chakra-ui/react";
import { Row, Col } from "antd";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";
import InputMoney from "components/Input/InputMoney/index";
import moment from "moment";
import { formatMoney } from "infra/helpers/number";
import colors from "core/resources/theme/colors";
import useSaveConsumoUnidade from "core/features/consumo/hooks/useSaveConsumoUnidade";
import { useInvalidateQuery } from "infra/packages/react-query";
import { getValorConsumo } from "core/features/consumo/helpers/getValorAplicadoConsumo";

interface CardUnidadeProps {
  consumo: ConsumoDto;
  unidade: ConsumoUnidade;
  editable: boolean;
  defaultValue?: number;
  onChangeMedida?: (v: number) => any;
}

function InfoSection({
  titulo,
  descricao,
  justify,
}: {
  titulo: string;
  descricao: React.ReactNode;
  justify?: "start" | "end";
}) {
  return (
    <Flex direction="column" mb="3">
      <Text color="#768398" fontSize="12px" textAlign={justify}>
        {titulo}
      </Text>
      <Flex
        fontSize="14px"
        justifyContent={justify}
        mt="auto"
        h={"30px"}
        alignItems="center"
      >
        {descricao}
      </Flex>
    </Flex>
  );
}

function CardUnidade({
  consumo,
  unidade,
  editable,
  defaultValue,
  onChangeMedida,
}: CardUnidadeProps) {
  const { doInvalidateQueryWithStringKey } = useInvalidateQuery();
  const [valorMedidaAtual, setValorMedidaAtual] = useState(
    defaultValue || unidade.medidaAtual || 0
  );

  const {
    mutate: saveUnidade,
    error,
    isLoading,
  } = useSaveConsumoUnidade(consumo.id, unidade.unidadeId);

  const onChange = (v?: number) => {
    setValorMedidaAtual(v ?? 0);
    onChangeMedida?.(v ?? 0);
  };

  const onSave = () => {
    saveUnidade(
      {
        medidaAtual: valorMedidaAtual,
      },
      {
        onSuccess: () =>
          doInvalidateQueryWithStringKey(`/v1/consumo/${consumo.id}/dashboard`),
      }
    );
  };

  const diferencaConsumo = Math.max(
    (valorMedidaAtual ?? 0) - (unidade.consumoUnidadeMesAnterior ?? 0),
    0
  );

  const valorMonetario = getValorConsumo(diferencaConsumo, consumo);

  return (
    <CardDefault mb={6} px={3} pt={2} h="80px">
      <Box as={Row} gutter={16}>
        <Box as={Col} xs={7}>
          <InfoSection
            titulo="Unidade"
            descricao={formatBlocoQuadra(unidade.numero, unidade.blocoQuadra)}
          />
        </Box>
        <Box as={Col} xs={5}>
          <InfoSection
            titulo={`Medição em ${moment(consumo.mes, "YYYY-MM")
              .subtract(1, "month")
              .format("MMMM")}`}
            descricao={(unidade.consumoUnidadeMesAnterior ?? 0).toFixed(3)}
            justify="end"
          />
        </Box>
        <Box as={Col} xs={5}>
          <InfoSection
            titulo={`Medição em ${moment(consumo.mes, "YYYY-MM").format(
              "MMMM"
            )}`}
            descricao={
              editable ? (
                <Flex gap={2}>
                  <InputMoney
                    decimalScale={3}
                    decimalsLimit={3}
                    value={valorMedidaAtual ?? undefined}
                    onChange={onChange}
                    onBlur={onSave}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter") {
                        onSave();
                      }
                    }}
                    style={{
                      height: 30,
                      textAlign: "right",
                    }}
                    isInvalid={!!error}
                    title={error ? "Ocorreu um erro ao salvar" : undefined}
                  />
                  <Flex w="30px" h="30px" mr="-40px" alignItems="center">
                    {isLoading ? (
                      <Spinner size="sm" color={colors.primary} />
                    ) : null}
                  </Flex>
                </Flex>
              ) : (
                valorMedidaAtual?.toFixed(3)
              )
            }
            justify="end"
          />
        </Box>
        <Box as={Col} xs={7}>
          <Flex justifyContent="flex-end">
            <InfoSection
              titulo={`Consumo em ${moment(consumo.mes, "YYYY-MM").format(
                "MMMM"
              )}`}
              descricao={
                <Flex flexDirection="column">
                  <Text textAlign="right" marginBottom={-1}>
                    {diferencaConsumo?.toFixed(3)}
                  </Text>
                  <Text color={colors.black300} textAlign="right">
                    {formatMoney(valorMonetario)}
                  </Text>
                </Flex>
              }
              justify="end"
            />
          </Flex>
        </Box>
      </Box>
    </CardDefault>
  );
}

export default memo(CardUnidade);
